import { UtagHelper } from './utagHelper';

declare global {
  interface Window {
    _pxOnCaptchaSuccess: (isValid: boolean) => void;
  }
}

export const botDetectionServiceUiConstants = {
  apiKey: {
    prod: 'PX6yz0QMxV',
    nonProd: 'PXzVPXa9Qu',
  },
};

export type BotDetectionAppearance = {
  challenge?: {
    translation?: {
      [languageCode: string]: {
        btn?: string;
        failed?: string;
        ac_1?: string;
        ac_2?: string;
        al_1?: string;
        al_2?: string;
        btn_done?: string;
      };
    };
    view?: {
      preset?: number;
      width?: number | string;
      height?: number | string;
      padding?: string;
      margin?: string;
      parts?: number;
      css?: string[];
      backgroundColor?: string;
      fillColor?: string;
      borderColor?: string;
      borderWidth?: number;
      borderRadius?: number;
      buttonBorderWidthOnFocus?: number;
      textColor?: string;
      texSize?: number;
      textFont?: string;
      fontWeight?: number | string;
      animation?: boolean;
      checkmarkThickness?: string;
      checkmarkHeight?: string;
      checkmarkWidth?: string;
      targetColor?: string;
      textTransform?: string;
    };
    context?: {
      logoImgSrc?: string;
      fontLinks?: string[];
      headerText?: string;
      headerColor?: string;
      headerFontSize?: string;
      headerFontFamily?: string;
      headerFontWeight?: string;
      messageText?: string;
      messageColor?: string;
      messageFontSize?: string;
      messageFontFamily?: string;
      messageFontWeight?: string;
      failed?: {
        fontSize?: string;
        color?: string;
      };
    };
  };
};

export type BotDetectionUtils = {
  loadComplete: Promise<void>;
  setChallengeAppearance: (appearance: BotDetectionAppearance) => void;
  setCaptchaResponseHandler: (handler: (isValid: boolean) => void) => void;
  onChallengeResult: (handler: (isValid: boolean) => void) => () => void;
  getHeaders(): { [key: string]: string };
};

let _botChallengeRetryResolver: ((isValid: boolean) => void) | null = null;

export const setBotChallengeRetryResolver = (resolver: (isValid: boolean) => void) => {
  _botChallengeRetryResolver = resolver;
};

export const invokeBotChallengeRetryResolver = (isValid: boolean) => {
  if (_botChallengeRetryResolver) {
    if (isValid) {
      const resolver = _botChallengeRetryResolver;
      _botChallengeRetryResolver = null;
      resolver(true);
    } else {
      _botChallengeRetryResolver(false);
    }
  }
};

export const clearBotChallengeRetryResolver = () => {
  _botChallengeRetryResolver = null;
};

export const hasBotChallengeRetryResolver = (): boolean => {
  return _botChallengeRetryResolver !== null;
};

const challengeResultHandlers: Array<(isValid: boolean) => void> = [];

export const registerChallengeResultHandler = (handler: (isValid: boolean) => void) => {
  challengeResultHandlers.push(handler);
  return () => {
    const idx = challengeResultHandlers.indexOf(handler);
    if (idx > -1) {
      challengeResultHandlers.splice(idx, 1);
    }
  };
};

export const clearChallengeResultHandlers = () => {
  challengeResultHandlers.length = 0;
};

let _isPreRenderChallenge = false;

export const isPreRenderChallenge = (): boolean => _isPreRenderChallenge;

export const resetPreRenderChallenge = (): void => {
  _isPreRenderChallenge = false;
};

export const handleChallengeResult = (isValid: boolean) => {
  if (isValid) {
    const currentValues = (window as any).aceMediaTagValues || {};
    if (_isPreRenderChallenge && !hasBotChallengeRetryResolver() && currentValues.page) {
      _isPreRenderChallenge = false;
      UtagHelper.tag({ ...currentValues, botdet: 'success' });
    } else {
      _isPreRenderChallenge = false;
      UtagHelper.setBotDetectionStatus('success');
    }
  } else {
    const page = (window as any).aceMediaTagValues?.page || '';
    UtagHelper.fireBotTag('failure', {
      error: `${page}-bot verification failed`,
    });
  }
  challengeResultHandlers.forEach((handler) => {
    try {
      handler(isValid);
    } catch (_) {}
  });
  invokeBotChallengeRetryResolver(isValid);
};

const MAX_PRE_RENDER_POLL_ATTEMPTS = 50;

export const checkAndFirePreRenderDetected = () => {
  if (hasBotChallengeRetryResolver()) {
    return;
  }
  _isPreRenderChallenge = true;
  let attempts = 0;
  const waitForPageData = () => {
    const tag = (window as any).aceMediaTagValues;
    if (tag?.page) {
      UtagHelper.fireBotTag('security check');
    } else if (attempts < MAX_PRE_RENDER_POLL_ATTEMPTS) {
      attempts++;
      setTimeout(waitForPageData, 100);
    }
  };
  waitForPageData();
};

export const setupPreRenderDetection = (targetNode?: Node): MutationObserver | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  if (document.getElementById('px-captcha')) {
    checkAndFirePreRenderDetected();
    return null;
  }
  const nodeToObserve = targetNode || document.body;
  if (!nodeToObserve) {
    return null;
  }
  const observer = new MutationObserver(() => {
    if (document.getElementById('px-captcha')) {
      observer.disconnect();
      checkAndFirePreRenderDetected();
    }
  });
  observer.observe(nodeToObserve, { childList: true, subtree: true });
  return observer;
};

export const tryInitBotDetection = (log: (str: string, data: object) => void, appId: string) => {
  try {
    return initBotDetection(appId);
  } catch (error: any) {
    log('Error while initializing bot detection', { error: error, errorMessage: error?.message });
  }
};

export const initBotDetection = (appId: string): BotDetectionUtils => {
  const setChallengeAppearance = (appearance: BotDetectionAppearance) => {
    (window as any)['_' + appId] = appearance;
  };

  const { setCaptchaResponseHandler, onChallengeResult } = createCaptchaResponseHander();

  const loadComplete = createInitScriptTag(appId);

  const getHeaders = createGetHeaders();

  return {
    loadComplete,
    setCaptchaResponseHandler,
    onChallengeResult,
    getHeaders,
    setChallengeAppearance,
  };
};

const createCaptchaResponseHander = () => {
  let captchaResponseHandler: ((isValid: boolean) => void) | null = null;

  window._pxOnCaptchaSuccess = function (isValid) {
    handleChallengeResult(isValid);
    if (captchaResponseHandler) {
      captchaResponseHandler(isValid);
    }
  };

  return {
    setCaptchaResponseHandler: (handler: (isValid: boolean) => void) => {
      captchaResponseHandler = handler;
    },
    onChallengeResult: (handler: (isValid: boolean) => void) => {
      return registerChallengeResultHandler(handler);
    },
  };
};

const createInitScriptTag = (appId: string): Promise<void> => {
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript === undefined || firstScript === null || firstScript.parentNode === null) {
    throw new Error('Failed to find first script element');
  }
  const parentNode = firstScript.parentNode;
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.addEventListener('load', () => {
      resolve();
    });
    script.addEventListener('error', (event) => {
      reject(`Error loading human bot detection script: ${event.type}: ${event.message}`);
    });
    script.async = true;
    script.src = `//client.px-cloud.net/${appId}/main.min.js`;
    parentNode.insertBefore(script, firstScript);
  });
};

const createGetHeaders = () => {
  const cookiesToSync = ['_px2', '_px3', '_pxhd', '_pxvid', 'pxcts'];

  const getCookieHeader = (): string => {
    return document.cookie
      .split(/;\s?/)
      .reduce((accumulator, cookie) => {
        const cookieName = cookie.split('=')[0];
        cookiesToSync.indexOf(cookieName) > -1 && (accumulator += cookie + '; ');
        return accumulator;
      }, '')
      .slice(0, -2);
  };

  return (): { [key: string]: string } => ({
    'x-px-cookies': getCookieHeader(),
  });
};

export const botDetectionChallengeAppearance: BotDetectionAppearance = {
  challenge: {
    context: {
      logoImgSrc: '/insurance/auto/olb/orbit-logo.png',
      headerText: 'Security check',
      headerFontSize: '24px',
      headerColor: '#404040',
      headerFontWeight: '700',
      messageText:
        'To verify that you are a human, please press and hold to continue. For accessibility help, click the blue button with an icon.',
      messageFontSize: '18px',
      messageColor: '#000000',
      messageFontWeight: '400',
    },
    translation: {
      default: {
        btn: 'Press & hold',
      },
    },
    view: {
      fillColor: '#3964b1',
      backgroundColor: '#ffffff',
      borderColor: '#1C4DA6',
      borderRadius: 50,
      preset: 1,
      borderWidth: 1,
      textColor: '#1C4DA6',
      texSize: 16,
      targetColor: '#ffffff',
      css: ['https://fonts.googleapis.com/css?family=Roboto:400,500&display=swap'],
    },
  },
};
